import { BILLIN_SERVICE_ID } from "@/lib/billinConstants";
import {
  BILLIN_SCROLL_TO_GOOGLE_SHEETS,
  buildBillinDestination,
} from "@/lib/billinGoRoutes";
import {
  buildGoogleIntegrationsExternalPath,
  navigateToGoogleIntegrationsConnect,
} from "@/lib/googleIntegrationsConnect";
import {
  hasClientAuthToken,
  readClientAuthTokenForIntegrationsConnect,
} from "@/lib/authSession";
import { getCanonicalOrigin } from "@/lib/getCanonicalOrigin";
import {
  clearSigninOAuthLoginNextPath,
  clearSigninPendingCallbackUrl,
} from "@/lib/signinCallbackUrl";
import {
  isGoogleWorkspaceSheetsReconnectPass,
  type GoogleIntegrationStatus,
} from "@/lib/googleIntegrationStatus";
import {
  fetchGoogleIntegrationStatus,
  invalidateGoogleIntegrationStatusCache,
  prefetchGoogleIntegrationStatus,
  readGoogleIntegrationStatusCacheAgeMs,
  readGoogleIntegrationStatusCacheSync,
} from "@/lib/googleIntegrationStatusFetch";
import {
  assignInPlaceAppLocationIfLineWebView,
  isLiffSdkReportedInClient,
  looksLikeLineEmbeddedBrowser,
  shouldShowSigninLineWebViewExternalModal,
} from "@/lib/openLineExternalBrowser";
import {
  persistLastWorkspace,
  readLastWorkspace,
} from "@/lib/lastWorkspaceStorage";
import { resolveBillinWorkspaceIdForSignin } from "@/lib/resolveWorkspaceForService";
import {
  probeSigninLiffClientEnvKind,
  resetSigninLiffReady,
  warmSigninLineProfileLikeBilllinGo,
} from "@/lib/signinLineLiff";
import { getSigninAuthTokenForApi } from "@/lib/signinPendingAuth";

export const SIGNIN_SHEETS_RECONNECT_SESSION_KEY =
  "signin_sheets_reconnect_pending_v1";

/** กลับมา `/signin?force=true&auto=1` หลัง login บน `/signin` ธรรมดา */
export const SIGNIN_SHEETS_RECONNECT_AFTER_LOGIN_PATH_KEY =
  "signin_sheets_reconnect_after_login_path_v1";

/** Workspace used for Sheets reconnect checks when URL has no `workspaceId`. */
export const SIGNIN_SHEETS_RECONNECT_WORKSPACE_KEY =
  "signin_sheets_reconnect_workspace_v1";

export const SIGNIN_SHEETS_RECONNECT_TITLE = "สิทธิ์หมดอายุ";

export const SIGNIN_SHEETS_RECONNECT_BRAND = "Google Sheets";

export const SIGNIN_SHEETS_RECONNECT_BODY =
  "กรุณาเชื่อมต่อ Google ใหม่อีกครั้ง เพื่อให้บิลลินจัดการสเปรดชีตได้ต่อ";

export const SIGNIN_SHEETS_RECONNECT_LINE_SUCCESS_TITLE = "ขอสิทธิ์สำเร็จ";

export const SIGNIN_SHEETS_RECONNECT_LINE_SUCCESS_SUBTITLE = "เชื่อมต่อสำเร็จ";

export const SIGNIN_SHEETS_RECONNECT_LINE_SUCCESS_BODY =
  "ปิดหน้าต่างแล้วคุยกับบิลลินต่อได้เลยครับ";

/** Success screen inside LINE LIFF / In-app WebView (same entry as sheets-expired). */
export const SIGNIN_SHEETS_RECONNECT_LINE_COMPLETE_PATH =
  "/signin/sheets-expired/complete";

/** @deprecated Legacy external-browser complete URL — prefer LINE complete path. */
export const SIGNIN_SHEETS_RECONNECT_COMPLETE_QUERY = "sheetsReconnect";

export const SIGNIN_SHEETS_RECONNECT_AWAITING_OAUTH_KEY =
  "signin_sheets_reconnect_awaiting_oauth_v1";

export const SIGNIN_SHEETS_RECONNECT_OAUTH_DONE_KEY =
  "signin_sheets_reconnect_oauth_done_v1";

export function resolveSigninSheetsReconnectLineLiffId(): string {
  return (process.env.NEXT_PUBLIC_LINE_LIFF_ID_SIGNIN || "").trim();
}

export function buildSigninSheetsReconnectLinePostOAuthLiffUrl(
  postOAuthPath: string = SIGNIN_SHEETS_RECONNECT_LINE_COMPLETE_PATH,
): string | null {
  const liffId = resolveSigninSheetsReconnectLineLiffId();
  if (!liffId || !liffId.includes("-")) return null;
  try {
    const u = new URL(`https://liff.line.me/${liffId}`);
    u.searchParams.set("path", postOAuthPath);
    return u.toString();
  } catch {
    return null;
  }
}

/** `next` for `/oauth/close` — LIFF deep-link back to the signin webview. */
export function buildSigninSheetsReconnectLinePostOAuthNextUrl(): string {
  const liffUrl = buildSigninSheetsReconnectLinePostOAuthLiffUrl();
  if (liffUrl) return liffUrl;
  const base =
    getCanonicalOrigin() ||
    (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}${SIGNIN_SHEETS_RECONNECT_LINE_COMPLETE_PATH}`;
}

export function isSigninSheetsReconnectLinePostOAuthNext(
  nextUrl: string | null | undefined,
): boolean {
  const raw = (nextUrl ?? "").trim();
  if (!raw) return false;
  return (
    raw.includes("/signin/sheets-expired/complete") ||
    raw.includes("sheets-expired%2Fcomplete")
  );
}

export function markSigninSheetsReconnectAwaitingOAuth(): void {
  if (typeof window === "undefined") return;
  invalidateGoogleIntegrationStatusCache();
  try {
    sessionStorage.setItem(SIGNIN_SHEETS_RECONNECT_AWAITING_OAUTH_KEY, "1");
    localStorage.setItem(SIGNIN_SHEETS_RECONNECT_AWAITING_OAUTH_KEY, "1");
  } catch {
    // ignore
  }
}

export function clearSigninSheetsReconnectAwaitingOAuth(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(SIGNIN_SHEETS_RECONNECT_AWAITING_OAUTH_KEY);
    localStorage.removeItem(SIGNIN_SHEETS_RECONNECT_AWAITING_OAUTH_KEY);
    localStorage.removeItem(SIGNIN_SHEETS_RECONNECT_OAUTH_DONE_KEY);
  } catch {
    // ignore
  }
}

export function isSigninSheetsReconnectAwaitingOAuth(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return (
      sessionStorage.getItem(SIGNIN_SHEETS_RECONNECT_AWAITING_OAUTH_KEY) ===
        "1" ||
      localStorage.getItem(SIGNIN_SHEETS_RECONNECT_AWAITING_OAUTH_KEY) === "1"
    );
  } catch {
    return false;
  }
}

export function markSigninSheetsReconnectOAuthDone(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      SIGNIN_SHEETS_RECONNECT_OAUTH_DONE_KEY,
      String(Date.now()),
    );
  } catch {
    // ignore
  }
}

export function consumeSigninSheetsReconnectOAuthDone(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const v = localStorage.getItem(SIGNIN_SHEETS_RECONNECT_OAUTH_DONE_KEY);
    if (!v) return false;
    localStorage.removeItem(SIGNIN_SHEETS_RECONNECT_OAUTH_DONE_KEY);
    return true;
  } catch {
    return false;
  }
}

/** Integrations OAuth `returnTo` — LINE sheets reconnect skips `/oauth/close`. */
export function buildIntegrationsOAuthReturnTo(opts: {
  canonicalOrigin: string;
  lineEmbedded: boolean;
  sheetsReconnect: boolean;
  nextUrl: string;
}): string {
  if (opts.sheetsReconnect && opts.lineEmbedded) {
    return buildSigninSheetsReconnectLinePostOAuthNextUrl();
  }
  return `${opts.canonicalOrigin}/oauth/close?next=${encodeURIComponent(
    opts.nextUrl,
  )}`;
}

/** postMessage from `/oauth/close` or `/connect/callback` (cross-origin opener). */
export const SIGNIN_SHEETS_RECONNECT_OAUTH_SUCCESS_MESSAGE =
  "signin-sheets-reconnect-oauth-success";

export function isSigninSheetsReconnectOAuthSuccessMessage(
  data: unknown,
): boolean {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { type?: string }).type ===
      SIGNIN_SHEETS_RECONNECT_OAUTH_SUCCESS_MESSAGE
  );
}

/** Notify the LINE webview that started `/login/external` (opener may be cross-origin). */
export function notifySigninSheetsReconnectOAuthSuccessToOpener(): void {
  if (typeof window === "undefined") return;
  try {
    const opener = window.opener as Window | null;
    if (!opener || opener.closed) return;
    opener.postMessage(
      { type: SIGNIN_SHEETS_RECONNECT_OAUTH_SUCCESS_MESSAGE },
      "*",
    );
  } catch {
    // ignore
  }
}

/** Route the original LINE webview to the sheets reconnect success screen. */
export function navigateSigninSheetsReconnectLineComplete(): void {
  clearSigninSheetsReconnectAwaitingOAuth();
  clearSigninSheetsReconnectIntent();
  if (typeof window === "undefined") return;

  const path = SIGNIN_SHEETS_RECONNECT_LINE_COMPLETE_PATH;
  const base = getCanonicalOrigin() || window.location.origin;

  // Already inside the app (incl. LINE in-app webview on canonical origin).
  if (window.location.hostname !== "liff.line.me") {
    window.location.replace(`${base}${path}`);
    return;
  }

  const liffUrl = buildSigninSheetsReconnectLinePostOAuthLiffUrl(path);
  if (liffUrl) {
    window.location.replace(liffUrl);
    return;
  }
  window.location.replace(`${base}${path}`);
}

export function signalSigninSheetsReconnectOAuthComplete(): void {
  markSigninSheetsReconnectOAuthDone();
  notifySigninSheetsReconnectOAuthSuccessToOpener();
}

export function tryNotifySheetsReconnectFromIntegrationsRedirect(
  redirectUrl: string,
): void {
  try {
    const u = new URL(redirectUrl, getCanonicalOrigin() || undefined);
    if (u.pathname.endsWith("/oauth/close")) {
      const nextParam = u.searchParams.get("next");
      if (isSigninSheetsReconnectLinePostOAuthNext(nextParam)) {
        signalSigninSheetsReconnectOAuthComplete();
      }
      return;
    }
  } catch {
    // ignore
  }
  if (isSigninSheetsReconnectLinePostOAuthNext(redirectUrl)) {
    signalSigninSheetsReconnectOAuthComplete();
  }
}

/** @deprecated Use tryNotifySheetsReconnectFromIntegrationsRedirect */
export function tryNotifySheetsReconnectOpenerFromOAuthCloseRedirect(
  redirectUrl: string,
): void {
  tryNotifySheetsReconnectFromIntegrationsRedirect(redirectUrl);
}

export type SigninSheetsReconnectOptions = {
  /** Set only when `callbackUrl` was passed on `/signin?force=true`. */
  callbackUrl?: string;
  serviceId: string;
  auto: boolean;
};

/** `auto=1` on `/signin?force=true` — only skip /signin when a usable JWT exists. */
export function canAutoStartSigninSheetsReconnect(): boolean {
  return Boolean(readClientAuthTokenForIntegrationsConnect());
}

function resolveSigninSheetsReconnectWorkspaceCallbackPath(
  urlSearch: string | null = null,
): string {
  const fields = collectSigninSheetsReconnectSearchFields(urlSearch);
  const explicit = normalizeExplicitCallback(fields.callbackUrl);
  if (explicit) return explicit;

  const lastWorkspace = readLastWorkspace()?.trim();
  if (lastWorkspace) {
    return `/?workspaceId=${lastWorkspace}`;
  }
  return "/";
}

/** Same shape as AuthWrapper — `/signin?callbackUrl=/?workspaceId=…` after logout. */
export function buildSigninUrlWithWorkspaceCallback(
  urlSearch: string | null = null,
): string {
  const callback = resolveSigninSheetsReconnectWorkspaceCallbackPath(urlSearch);
  // Marker: allows resuming sheets force entry after cross-tab logout.
  return `/signin?callbackUrl=${encodeURIComponent(callback)}&sheetsReconnectResume=1`;
}

export function buildSigninSheetsForceAutoEntryPath(
  options: Partial<
    Pick<SigninSheetsReconnectOptions, "auto" | "serviceId" | "callbackUrl">
  > = {},
): string {
  const q = new URLSearchParams();
  q.set("force", "true");
  q.set("auto", options.auto === false ? "0" : "1");
  const sid = (options.serviceId || BILLIN_SERVICE_ID).trim();
  if (sid && sid !== BILLIN_SERVICE_ID) {
    q.set("serviceId", sid);
  }
  const explicitCallback = normalizeExplicitCallback(options.callbackUrl);
  if (explicitCallback) {
    q.set("callbackUrl", explicitCallback);
  }
  return `/signin?${q.toString()}`;
}

export function buildSigninSheetsReconnectReturnPath(
  options: SigninSheetsReconnectOptions,
): string {
  return buildSigninSheetsForceAutoEntryPath(options);
}

export function persistSigninSheetsReconnectAfterLoginPath(path: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(SIGNIN_SHEETS_RECONNECT_AFTER_LOGIN_PATH_KEY, path);
    localStorage.setItem(SIGNIN_SHEETS_RECONNECT_AFTER_LOGIN_PATH_KEY, path);
  } catch {
    // ignore
  }
}

export function readSigninSheetsReconnectAfterLoginPath(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw =
      sessionStorage
        .getItem(SIGNIN_SHEETS_RECONNECT_AFTER_LOGIN_PATH_KEY)
        ?.trim() ||
      localStorage
        .getItem(SIGNIN_SHEETS_RECONNECT_AFTER_LOGIN_PATH_KEY)
        ?.trim();
    if (!raw?.startsWith("/signin")) return null;
    return raw;
  } catch {
    return null;
  }
}

export function clearSigninSheetsReconnectAfterLoginPath(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(SIGNIN_SHEETS_RECONNECT_AFTER_LOGIN_PATH_KEY);
    localStorage.removeItem(SIGNIN_SHEETS_RECONNECT_AFTER_LOGIN_PATH_KEY);
  } catch {
    // ignore
  }
}

function readSigninSheetsReconnectAfterLoginSearch(): string | null {
  const pending = readSigninSheetsReconnectAfterLoginPath();
  if (!pending) return null;
  const qi = pending.indexOf("?");
  return qi >= 0 ? pending.slice(qi) : null;
}

export function hasPendingSigninSheetsForceAfterLoginResume(): boolean {
  const search = readSigninSheetsReconnectAfterLoginSearch();
  return Boolean(search && isSigninSheetsForceQuery(search));
}

export function isSigninSheetsReconnectResumeQuery(
  urlSearch: string | null = typeof window !== "undefined"
    ? window.location.search || ""
    : "",
): boolean {
  if (!urlSearch) return false;
  try {
    const raw = urlSearch.startsWith("?") ? urlSearch.slice(1) : urlSearch;
    return new URLSearchParams(raw).get("sheetsReconnectResume") === "1";
  } catch {
    return false;
  }
}

/** Block Billin auto-route while sheets reconnect resume is in-flight (JWT required). */
export function shouldDeferSigninAutoRouteForSheetsReconnect(
  urlSearch: string | null = typeof window !== "undefined"
    ? window.location.search || ""
    : "",
): boolean {
  if (!canAutoStartSigninSheetsReconnect()) return false;
  return isSigninSheetsReconnectSigninEntry(urlSearch);
}

/** Sheets reconnect entry on `/signin` — force URL, resume marker, or staged storage. */
export function isSigninSheetsReconnectSigninEntry(
  urlSearch: string | null = typeof window !== "undefined"
    ? window.location.search || ""
    : "",
): boolean {
  return (
    isSigninSheetsForceQuery(urlSearch) ||
    isSigninSheetsReconnectResumeQuery(urlSearch) ||
    hasPendingSigninSheetsForceAfterLoginResume() ||
    hasSigninSheetsReconnectIntent()
  );
}

function resolveSigninSheetsReconnectPendingForceSearch(): string {
  const pending = readSigninSheetsReconnectAfterLoginSearch();
  if (pending && isSigninSheetsForceQuery(pending)) return pending;
  const path = buildSigninSheetsForceAutoEntryPath({ auto: true });
  const qi = path.indexOf("?");
  return qi >= 0 ? path.slice(qi) : "?force=true&auto=1";
}

/**
 * Resume sheets reconnect from `/signin?callbackUrl=…&sheetsReconnectResume=1`,
 * a pending after-login path, or `/signin?force=true`.
 */
export async function resumeSigninSheetsReconnectIfPending(
  urlSearch: string | null = null,
): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const search =
    urlSearch ??
    (typeof window !== "undefined" ? window.location.search || "" : "");
  if (!canAutoStartSigninSheetsReconnect()) return false;
  if (shouldSuppressSigninSheetsReconnectAfterLoginResume(search)) return false;

  const onForceUrl = isSigninSheetsForceQuery(search);
  const onResumeUrl = isSigninSheetsReconnectResumeQuery(search);
  const hasPending = hasPendingSigninSheetsForceAfterLoginResume();
  const hasIntent = hasSigninSheetsReconnectIntent();

  if (!onForceUrl && !onResumeUrl && !hasPending && !hasIntent) return false;

  invalidateGoogleIntegrationStatusCache();

  if (!onForceUrl && tryResumeSigninSheetsReconnectAfterLogin()) {
    return true;
  }

  const forceSearch = onForceUrl
    ? search
    : resolveSigninSheetsReconnectPendingForceSearch();

  await resumeSigninSheetsForceFlowAfterAuth(forceSearch);
  return true;
}

/**
 * AuthWrapper-style `/signin?callbackUrl=…` without `force` — honor callbackUrl
 * after login, not a stale sheets reconnect return path — unless sheets logout
 * staged a pending `/signin?force=true&auto=1` resume.
 */
export function shouldSuppressSigninSheetsReconnectAfterLoginResume(
  urlSearch: string | null = typeof window !== "undefined"
    ? window.location.search || ""
    : "",
): boolean {
  if (!urlSearch) return false;
  const { callbackUrl } = collectSigninSheetsReconnectSearchFields(urlSearch);
  if (!callbackUrl) return false;
  if (isSigninSheetsForceQuery(urlSearch)) return false;
  try {
    const raw = urlSearch.startsWith("?") ? urlSearch.slice(1) : urlSearch;
    const params = new URLSearchParams(raw);
    // Only allow sheets reconnect to override callbackUrl when explicitly marked
    // (set by `releaseSigninSheetsForceEntryWhenLoggedOut`).
    if (params.get("sheetsReconnectResume") === "1") {
      return false;
    }
  } catch {
    // ignore
  }
  return true;
}

function isSigninSheetsReconnectAfterLoginResumeContext(
  urlSearch: string | null = typeof window !== "undefined"
    ? window.location.search || ""
    : "",
): boolean {
  if (shouldSuppressSigninSheetsReconnectAfterLoginResume(urlSearch)) {
    return false;
  }
  if (isSigninSheetsForceQuery(urlSearch)) return true;
  if (isSigninSheetsReconnectResumeQuery(urlSearch)) return true;
  if (typeof window !== "undefined") {
    if (window.location.pathname.startsWith("/signin/sheets-expired")) {
      return true;
    }
  }
  return Boolean(readSigninSheetsReconnectAfterLoginPath());
}

/** Keep `/signin?force=true&auto=1` across OAuth and cross-tab login. */
export function persistSigninSheetsForceEntryFromSearch(
  urlSearch: string | null,
): string | null {
  if (!urlSearch || !isSigninSheetsForceQuery(urlSearch)) return null;
  const options = readSigninSheetsReconnectOptions(urlSearch);
  const returnPath = buildSigninSheetsForceAutoEntryPath({
    auto: options.auto,
    serviceId: options.serviceId,
    ...(options.callbackUrl ? { callbackUrl: options.callbackUrl } : {}),
  });
  persistSigninSheetsReconnectAfterLoginPath(returnPath);
  stageSigninSheetsReconnectFromSearch(urlSearch);
  clearSigninOAuthLoginNextPath();
  clearSigninPendingCallbackUrl();
  prefetchSigninSheetsReconnectGoogleStatus();
  return returnPath;
}

/**
 * Stage sheets reconnect across OAuth when on `/signin?callbackUrl=…&sheetsReconnectResume=1`
 * (LINE redirect URI is registered as bare `/signin` — context must live in storage).
 */
export function persistSigninSheetsReconnectEntryFromSearch(
  urlSearch: string | null,
): string | null {
  if (!urlSearch) return null;

  const forced = persistSigninSheetsForceEntryFromSearch(urlSearch);
  if (forced) return forced;

  if (!isSigninSheetsReconnectResumeQuery(urlSearch)) return null;

  const forceReturnPath = buildSigninSheetsForceAutoEntryPath({ auto: true });
  persistSigninSheetsReconnectAfterLoginPath(forceReturnPath);
  const forceQuery = forceReturnPath.includes("?")
    ? forceReturnPath.slice(forceReturnPath.indexOf("?"))
    : "";
  if (forceQuery) {
    stageSigninSheetsReconnectFromSearch(forceQuery);
  } else {
    clearSigninSheetsReconnectIntent();
  }

  clearSigninOAuthLoginNextPath();
  clearSigninPendingCallbackUrl();

  const qs = urlSearch.startsWith("?") ? urlSearch : `?${urlSearch}`;
  return `/signin${qs}`;
}

/** Warm up `/integrations/google/status` when a JWT is available. */
export function prefetchSigninSheetsReconnectGoogleStatus(): void {
  if (typeof window === "undefined") return;
  if (!getSigninAuthTokenForApi()) return;
  prefetchGoogleIntegrationStatus();
}

/** OAuth `oauth_return_path` — preserves sheets reconnect entry query when present. */
export function buildSigninOAuthReturnUrlForCurrentEntry(
  urlSearch: string | null = typeof window !== "undefined"
    ? window.location.search || ""
    : "",
): string {
  const base =
    getCanonicalOrigin() ||
    (typeof window !== "undefined" ? window.location.origin : "");
  const entry = persistSigninSheetsReconnectEntryFromSearch(urlSearch);
  if (entry) return `${base}${entry}`;
  return `${base}/signin`;
}

/**
 * After sign-in on /signin — resume sheets force entry instead of Billin home.
 * Caller should `commit` JWT before status checks when token is still pending.
 */
export async function tryFinishSigninSheetsForceEntryAfterAuth(
  commit?: () => void | Promise<void>,
): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const search = window.location.search || "";
  const inSheetsForceEntry = isSigninSheetsReconnectSigninEntry(search);
  if (!inSheetsForceEntry) return false;

  if (commit) await commit();
  invalidateGoogleIntegrationStatusCache();

  if (isSigninSheetsForceQuery(search) && canAutoStartSigninSheetsReconnect()) {
    await resumeSigninSheetsForceFlowAfterAuth(search);
    return true;
  }

  if (hasPendingSigninSheetsForceAfterLoginResume()) {
    return tryResumeSigninSheetsReconnectAfterLogin();
  }

  if (canAutoStartSigninSheetsReconnect()) {
    await resumeSigninSheetsReconnectIfPending(search);
    return true;
  }

  return false;
}

export type SigninSheetsReconnectLoginNavigation =
  | "liff_assigned"
  | "navigated"
  | "external_modal";

/**
 * LINE/LIFF → prefetch profile + เปิดผ่าน signin LIFF endpoint ก่อน (เหมือน `navigateToSignin`).
 * In-place จากหน้าอื่น (เช่น sheets-expired) มัก init LIFF ผิด → หาโปรไฟล์ไม่เจอ
 */
export async function navigateToSigninPathWithLineProfileWarmup(
  signinPath: string,
): Promise<SigninSheetsReconnectLoginNavigation> {
  if (typeof window === "undefined") return "navigated";

  const base = getCanonicalOrigin() || window.location.origin;
  const rel = signinPath.startsWith("/") ? signinPath : `/${signinPath}`;

  resetSigninLiffReady();
  const warmup = await warmSigninLineProfileLikeBilllinGo(rel);
  if (warmup === "redirected") return "liff_assigned";

  const onSigninRoot =
    typeof window !== "undefined" && window.location.pathname === "/signin";
  if (
    onSigninRoot &&
    isLiffSdkReportedInClient() &&
    assignInPlaceAppLocationIfLineWebView(rel)
  ) {
    return "navigated";
  }

  const envKind = await probeSigninLiffClientEnvKind();
  if (shouldShowSigninLineWebViewExternalModal(envKind)) {
    return "external_modal";
  }

  window.location.replace(`${base}${rel}`);
  return "navigated";
}

/**
 * ไม่มี JWT — ไป `/signin?force=true&auto=1` ก่อน resume sheets flow.
 */
export async function navigateToSigninForSheetsReconnectLogin(
  options: SigninSheetsReconnectOptions,
): Promise<SigninSheetsReconnectLoginNavigation> {
  if (typeof window === "undefined") return "navigated";

  const returnPath = buildSigninSheetsReconnectReturnPath(options);
  persistSigninSheetsReconnectAfterLoginPath(returnPath);
  const query = returnPath.includes("?")
    ? returnPath.slice(returnPath.indexOf("?"))
    : "";
  if (query) {
    stageSigninSheetsReconnectFromSearch(query);
  }

  return navigateToSigninPathWithLineProfileWarmup(returnPath);
}

/** @deprecated Prefer `navigateToSigninForSheetsReconnectLogin` (LIFF profile prefetch). */
export function redirectToSigninForSheetsReconnectLogin(
  options: SigninSheetsReconnectOptions,
): void {
  void navigateToSigninForSheetsReconnectLogin(options);
}

export function tryResumeSigninSheetsReconnectAfterLogin(): boolean {
  if (typeof window === "undefined") return false;
  if (!readClientAuthTokenForIntegrationsConnect()) return false;

  const search = window.location.search || "";
  if (shouldSuppressSigninSheetsReconnectAfterLoginResume(search)) {
    clearSigninSheetsReconnectAfterLoginPath();
    return false;
  }
  if (!isSigninSheetsReconnectAfterLoginResumeContext(search)) {
    return false;
  }

  const returnPath = readSigninSheetsReconnectAfterLoginPath();
  if (!returnPath) return false;
  clearSigninSheetsReconnectAfterLoginPath();

  const current = `${window.location.pathname}${window.location.search}`;
  if (current === returnPath) {
    return false;
  }

  const base = getCanonicalOrigin() || window.location.origin;
  window.location.replace(`${base}${returnPath}`);
  return true;
}

function normalizeExplicitCallback(
  raw: string | null | undefined,
): string | undefined {
  const text = (raw ?? "").trim();
  if (text.startsWith("/") && !text.startsWith("//")) return text;
  return undefined;
}

const SIGNIN_SHEETS_RECONNECT_SERVICE_FALLBACK = `/service/${BILLIN_SERVICE_ID}?scrollTo=${BILLIN_SCROLL_TO_GOOGLE_SHEETS}`;

/** Post-reconnect destination — prefers `aera_last_workspace` home deep-link. */
export function resolveSigninSheetsReconnectCallbackUrl(
  options: Pick<SigninSheetsReconnectOptions, "callbackUrl"> = {},
): string {
  const explicit = normalizeExplicitCallback(options.callbackUrl);
  const lastWorkspace = readLastWorkspace()?.trim();
  if (lastWorkspace) {
    return buildBillinDestination("app", lastWorkspace, {
      scrollTo: BILLIN_SCROLL_TO_GOOGLE_SHEETS,
    });
  }
  if (explicit) return explicit;
  return SIGNIN_SHEETS_RECONNECT_SERVICE_FALLBACK;
}

/** `/login/external` opened from `/signin/sheets-expired` (see `reconnect=1` or `cancelTo`). */
export function isSigninSheetsReconnectExternalLogin(
  urlSearch: string | null | undefined,
): boolean {
  if (!urlSearch) return false;
  try {
    const raw = urlSearch.startsWith("?") ? urlSearch.slice(1) : urlSearch;
    const params = new URLSearchParams(raw);
    if (params.get("flow") !== "integrations") return false;
    if (params.get("reconnect") === "1") return true;
    const cancelTo = normalizeExplicitCallback(params.get("cancelTo"));
    return Boolean(cancelTo?.startsWith("/signin/sheets-expired"));
  } catch {
    return false;
  }
}

export function readSigninSheetsReconnectOptionsFromExternalLogin(
  urlSearch: string | null | undefined,
): SigninSheetsReconnectOptions {
  if (!urlSearch) {
    return { serviceId: BILLIN_SERVICE_ID, auto: false };
  }
  try {
    const raw = urlSearch.startsWith("?") ? urlSearch.slice(1) : urlSearch;
    const params = new URLSearchParams(raw);
    const explicitCallback = normalizeExplicitCallback(
      params.get("callbackUrl"),
    );
    return {
      ...(explicitCallback ? { callbackUrl: explicitCallback } : {}),
      serviceId: (params.get("serviceId") || BILLIN_SERVICE_ID).trim(),
      auto: isSigninSheetsReconnectAutoParam(params.get("auto")),
    };
  } catch {
    return { serviceId: BILLIN_SERVICE_ID, auto: false };
  }
}

export function isSigninSheetsReconnectCompleteSearch(
  urlSearch: string | null | undefined,
): boolean {
  if (!urlSearch) return false;
  try {
    const raw = urlSearch.startsWith("?") ? urlSearch.slice(1) : urlSearch;
    return (
      new URLSearchParams(raw).get(SIGNIN_SHEETS_RECONNECT_COMPLETE_QUERY) ===
      "1"
    );
  } catch {
    return false;
  }
}

export function isSigninSheetsReconnectCompletePath(pathname: string): boolean {
  return pathname === SIGNIN_SHEETS_RECONNECT_LINE_COMPLETE_PATH;
}

/**
 * Post-OAuth destination when starting from `/login/external` (sheets reconnect).
 * LINE LIFF / In-app WebView → success page; browser → `aera_last_workspace` home.
 */
export function resolveSigninSheetsReconnectPostOAuthPath(
  urlSearch: string | null | undefined,
  opts?: { lineEmbedded?: boolean },
): string {
  if (!isSigninSheetsReconnectExternalLogin(urlSearch)) {
    return resolveLoginExternalIntegrationsCallbackPath(urlSearch);
  }
  const lineEmbedded =
    opts?.lineEmbedded ??
    (typeof window !== "undefined" && looksLikeLineEmbeddedBrowser());
  if (lineEmbedded) {
    return SIGNIN_SHEETS_RECONNECT_LINE_COMPLETE_PATH;
  }
  return resolveSigninSheetsReconnectCallbackUrl(
    readSigninSheetsReconnectOptionsFromExternalLogin(urlSearch),
  );
}

/** Resolve post-OAuth destination on `/login/external?flow=integrations`. */
export function resolveLoginExternalIntegrationsCallbackPath(
  urlSearch: string | null | undefined,
): string {
  if (isSigninSheetsReconnectExternalLogin(urlSearch)) {
    return resolveSigninSheetsReconnectCallbackUrl(
      readSigninSheetsReconnectOptionsFromExternalLogin(urlSearch),
    );
  }
  try {
    const raw = urlSearch?.startsWith("?")
      ? urlSearch.slice(1)
      : (urlSearch ?? "");
    const params = new URLSearchParams(raw);
    const callbackRaw = params.get("callbackUrl");
    const fromQuery =
      callbackRaw?.startsWith("/") && !callbackRaw.startsWith("//")
        ? callbackRaw
        : "/";
    if (!callbackRaw || fromQuery === "/") {
      const wid = readLastWorkspace()?.trim();
      if (wid) {
        return buildBillinDestination("app", wid, {
          scrollTo: BILLIN_SCROLL_TO_GOOGLE_SHEETS,
        });
      }
    }
    return fromQuery;
  } catch {
    return "/";
  }
}

type SigninSheetsReconnectSearchFields = {
  force: string | null;
  auto: string | null;
  serviceId: string | null;
  callbackUrl: string | null;
  workspaceId: string | null;
};

/** Merge query fields from top-level URL, LIFF `path`, and `liff.state`. */
function collectSigninSheetsReconnectSearchFields(
  urlSearch: string | null,
): SigninSheetsReconnectSearchFields {
  const merged: SigninSheetsReconnectSearchFields = {
    force: null,
    auto: null,
    serviceId: null,
    callbackUrl: null,
    workspaceId: null,
  };

  const absorbInnerSearch = (queryPart: string) => {
    const q = queryPart.startsWith("?") ? queryPart.slice(1) : queryPart;
    if (!q.trim()) return;
    const inner = new URLSearchParams(q);
    if (!merged.force) merged.force = inner.get("force")?.trim() || null;
    if (!merged.auto) merged.auto = inner.get("auto")?.trim() || null;
    if (!merged.serviceId) {
      merged.serviceId = inner.get("serviceId")?.trim() || null;
    }
    if (!merged.callbackUrl) {
      merged.callbackUrl = inner.get("callbackUrl")?.trim() || null;
    }
    if (!merged.workspaceId) {
      merged.workspaceId = inner.get("workspaceId")?.trim() || null;
    }
  };

  const absorbEncodedPath = (pathValue: string) => {
    let decoded = pathValue;
    try {
      decoded = decodeURIComponent(pathValue);
    } catch {
      decoded = pathValue;
    }
    if (!decoded.startsWith("/")) return;
    const qi = decoded.indexOf("?");
    if (qi >= 0) absorbInnerSearch(decoded.slice(qi));
  };

  if (!urlSearch) return merged;

  try {
    const raw = urlSearch.startsWith("?") ? urlSearch.slice(1) : urlSearch;
    const topParams = new URLSearchParams(raw);
    merged.force = topParams.get("force")?.trim() || null;
    merged.auto = topParams.get("auto")?.trim() || null;
    merged.serviceId = topParams.get("serviceId")?.trim() || null;
    merged.callbackUrl = topParams.get("callbackUrl")?.trim() || null;
    merged.workspaceId = topParams.get("workspaceId")?.trim() || null;

    const rawPath = topParams.get("path");
    if (rawPath) absorbEncodedPath(rawPath);

    const rawState = topParams.get("liff.state");
    if (rawState) {
      try {
        let decodedState = rawState;
        try {
          decodedState = decodeURIComponent(rawState);
        } catch {
          decodedState = rawState;
        }
        const stateQs = decodedState.startsWith("?")
          ? decodedState.slice(1)
          : decodedState;
        const nested = new URLSearchParams(stateQs);
        const nestedPath = nested.get("path");
        if (nestedPath) absorbEncodedPath(nestedPath);
        else absorbInnerSearch(stateQs);
      } catch {
        // ignore
      }
    }
  } catch {
    // ignore
  }

  return merged;
}

export function isSigninSheetsForceQuery(urlSearch: string | null): boolean {
  const { force } = collectSigninSheetsReconnectSearchFields(urlSearch);
  return force === "true" || force === "1";
}

function isSigninSheetsReconnectAutoParam(
  auto: string | null | undefined,
): boolean {
  const value = auto?.trim().toLowerCase();
  return value === "1" || value === "true";
}

export function readSigninSheetsReconnectOptions(
  urlSearch: string | null,
): SigninSheetsReconnectOptions {
  if (!urlSearch) {
    return {
      serviceId: BILLIN_SERVICE_ID,
      auto: false,
    };
  }
  try {
    const fields = collectSigninSheetsReconnectSearchFields(urlSearch);
    const explicitCallback = normalizeExplicitCallback(fields.callbackUrl);
    const forced = isSigninSheetsForceQuery(urlSearch);
    const auto = fields.auto ? isSigninSheetsReconnectAutoParam(fields.auto) : forced;
    return {
      ...(explicitCallback ? { callbackUrl: explicitCallback } : {}),
      serviceId: (fields.serviceId || BILLIN_SERVICE_ID).trim(),
      auto,
    };
  } catch {
    return {
      serviceId: BILLIN_SERVICE_ID,
      auto: false,
    };
  }
}

export function resolveSigninSheetsReconnectWorkspaceId(
  urlSearch: string | null,
  options?: Pick<SigninSheetsReconnectOptions, "callbackUrl">,
): string | null {
  const fields = collectSigninSheetsReconnectSearchFields(urlSearch);
  const fromQuery = fields.workspaceId?.trim() || null;
  if (fromQuery) return fromQuery;

  const callbackUrl = options?.callbackUrl?.trim() || fields.callbackUrl?.trim();
  if (callbackUrl) {
    try {
      const base = getCanonicalOrigin() || "https://example.com";
      const cb = new URL(callbackUrl, base);
      const fromCallback = cb.searchParams.get("workspaceId")?.trim() || null;
      if (fromCallback) return fromCallback;
    } catch {
      // ignore
    }
  }

  const fromLast = readLastWorkspace()?.trim() || null;
  if (fromLast) return fromLast;

  return readPersistedSigninSheetsReconnectWorkspaceId();
}

export function persistSigninSheetsReconnectWorkspaceId(
  workspaceId: string,
): void {
  if (typeof window === "undefined") return;
  const wid = workspaceId?.trim();
  if (!wid) return;
  try {
    sessionStorage.setItem(SIGNIN_SHEETS_RECONNECT_WORKSPACE_KEY, wid);
    localStorage.setItem(SIGNIN_SHEETS_RECONNECT_WORKSPACE_KEY, wid);
    persistLastWorkspace(wid);
  } catch {
    // ignore
  }
}

export function readPersistedSigninSheetsReconnectWorkspaceId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw =
      sessionStorage.getItem(SIGNIN_SHEETS_RECONNECT_WORKSPACE_KEY)?.trim() ||
      localStorage.getItem(SIGNIN_SHEETS_RECONNECT_WORKSPACE_KEY)?.trim();
    return raw || null;
  } catch {
    return null;
  }
}

export function clearSigninSheetsReconnectWorkspaceId(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(SIGNIN_SHEETS_RECONNECT_WORKSPACE_KEY);
    localStorage.removeItem(SIGNIN_SHEETS_RECONNECT_WORKSPACE_KEY);
  } catch {
    // ignore
  }
}

function listSheetLinkedWorkspaceIds(
  status: GoogleIntegrationStatus,
): string[] {
  return Object.keys(status.sheetsByWorkspace).filter(
    (key) => key !== "__legacy__" && status.sheetsByWorkspace[key]?.trim(),
  );
}

/**
 * Resolve workspace for Sheets status checks — URL/callback/last pick, then API
 * (needed right after sign-in before `aera_last_workspace` is set).
 */
export async function ensureSigninSheetsReconnectWorkspaceResolved(
  urlSearch: string | null,
  options?: SigninSheetsReconnectOptions,
  status?: GoogleIntegrationStatus | null,
): Promise<string | null> {
  const resolvedOptions = options ?? readSigninSheetsReconnectOptions(urlSearch);
  const sync = resolveSigninSheetsReconnectWorkspaceId(
    urlSearch,
    resolvedOptions,
  );
  if (sync) {
    persistSigninSheetsReconnectWorkspaceId(sync);
    return sync;
  }

  if (!canAutoStartSigninSheetsReconnect()) return null;

  const serviceId = (resolvedOptions.serviceId || BILLIN_SERVICE_ID).trim();
  try {
    const fromBillin = await resolveBillinWorkspaceIdForSignin(serviceId);
    if (fromBillin) {
      persistSigninSheetsReconnectWorkspaceId(fromBillin);
      return fromBillin;
    }
  } catch {
    // ignore — fall through to sheet inference
  }

  const statusForInference =
    status ??
    readGoogleIntegrationStatusCacheSync() ??
    (await fetchGoogleIntegrationStatus().catch(() => null));
  if (statusForInference) {
    const sheetWorkspaceIds = listSheetLinkedWorkspaceIds(statusForInference);
    if (sheetWorkspaceIds.length === 1) {
      persistSigninSheetsReconnectWorkspaceId(sheetWorkspaceIds[0]);
      return sheetWorkspaceIds[0];
    }
  }

  return null;
}

/** True when `/integrations/google/status` reports a sheet link for this workspace. */
export async function isSigninSheetsReconnectAlreadyConnected(
  urlSearch: string | null,
  options?: SigninSheetsReconnectOptions,
  opts?: { fresh?: boolean },
): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (!canAutoStartSigninSheetsReconnect()) return false;

  const resolvedOptions = options ?? readSigninSheetsReconnectOptions(urlSearch);
  const workspaceId = await ensureSigninSheetsReconnectWorkspaceResolved(
    urlSearch,
    resolvedOptions,
  );

  const isConnectedForWorkspace = (status: GoogleIntegrationStatus) =>
    isGoogleWorkspaceSheetsReconnectPass(status, workspaceId);

  try {
    if (!opts?.fresh) {
      const cachedStatus = readGoogleIntegrationStatusCacheSync();
      const cacheAgeMs = readGoogleIntegrationStatusCacheAgeMs();
      if (cachedStatus && cacheAgeMs !== null && cacheAgeMs < 5 * 60 * 1000) {
        if (isConnectedForWorkspace(cachedStatus)) {
          return true;
        }
      }
    }

    const status = await fetchGoogleIntegrationStatus(
      opts?.fresh ? { force: true } : undefined,
    );
    return isConnectedForWorkspace(status);
  } catch {
    return false;
  }
}

/**
 * When Sheets is already connected for this workspace, open the success page.
 * Uses LIFF-aware navigation (works on `liff.line.me` and in-app webview).
 */
export async function tryNavigateSigninSheetsReconnectCompleteIfConnected(
  urlSearch: string | null,
  opts?: { requireForce?: boolean; requireAuto?: boolean; fresh?: boolean },
): Promise<boolean> {
  if (opts?.requireForce && !isSigninSheetsForceQuery(urlSearch)) {
    return false;
  }

  const options = readSigninSheetsReconnectOptions(urlSearch);
  if (opts?.requireAuto && !options.auto) return false;

  const connected = await isSigninSheetsReconnectAlreadyConnected(
    urlSearch,
    options,
    { fresh: opts?.fresh },
  );
  if (!connected) return false;

  navigateSigninSheetsReconnectLineComplete();
  return true;
}

export function readSigninSheetsReconnectIntent(): SigninSheetsReconnectOptions | null {
  if (typeof window === "undefined") return null;
  try {
    const raw =
      sessionStorage.getItem(SIGNIN_SHEETS_RECONNECT_SESSION_KEY)?.trim() ||
      localStorage.getItem(SIGNIN_SHEETS_RECONNECT_SESSION_KEY)?.trim();
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SigninSheetsReconnectOptions>;
    if (!parsed || typeof parsed !== "object") return null;
    const explicitCallback = normalizeExplicitCallback(parsed.callbackUrl);
    return {
      ...(explicitCallback ? { callbackUrl: explicitCallback } : {}),
      serviceId: (parsed.serviceId || BILLIN_SERVICE_ID).trim(),
      auto: parsed.auto === true,
    };
  } catch {
    return null;
  }
}

export function hasSigninSheetsReconnectIntent(): boolean {
  return readSigninSheetsReconnectIntent() !== null;
}

export function persistSigninSheetsReconnectIntent(
  options: SigninSheetsReconnectOptions,
): void {
  if (typeof window === "undefined") return;
  try {
    const payload = JSON.stringify(options);
    sessionStorage.setItem(SIGNIN_SHEETS_RECONNECT_SESSION_KEY, payload);
    localStorage.setItem(SIGNIN_SHEETS_RECONNECT_SESSION_KEY, payload);
  } catch {
    // ignore
  }
}

export function clearSigninSheetsReconnectIntent(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(SIGNIN_SHEETS_RECONNECT_SESSION_KEY);
    localStorage.removeItem(SIGNIN_SHEETS_RECONNECT_SESSION_KEY);
  } catch {
    // ignore
  }
}

/** Stage intent from `/signin?force=true` (Sheets grant expired). */
export function stageSigninSheetsReconnectFromSearch(
  urlSearch: string,
): SigninSheetsReconnectOptions | null {
  if (!isSigninSheetsForceQuery(urlSearch)) return null;
  const options = readSigninSheetsReconnectOptions(urlSearch);
  persistSigninSheetsReconnectIntent(options);
  return options;
}

export function stripSigninSheetsForceFromUrlBar(): void {
  if (typeof window === "undefined") return;
  try {
    const u = new URL(window.location.href);
    if (!u.searchParams.has("force")) return;
    u.searchParams.delete("force");
    const qs = u.searchParams.toString();
    window.history.replaceState(
      null,
      "",
      `${u.pathname}${qs ? `?${qs}` : ""}${u.hash}`,
    );
  } catch {
    // ignore
  }
}

export function isSigninSheetsForceAutoEntry(
  urlSearch: string | null = null,
): boolean {
  const search =
    urlSearch ??
    (typeof window !== "undefined" ? window.location.search || "" : "");
  if (!search) return false;
  return (
    isSigninSheetsForceQuery(search) &&
    readSigninSheetsReconnectOptions(search).auto
  );
}

/**
 * After JWT is available (incl. cross-tab sign-in), continue the sheets reconnect entry.
 * Checks Google status once — connected → complete, else → sheets-expired (no extra hop).
 */
export async function resumeSigninSheetsForceFlowAfterAuth(
  urlSearch: string | null = null,
): Promise<void> {
  if (typeof window === "undefined") return;
  const search =
    urlSearch ??
    (typeof window !== "undefined" ? window.location.search || "" : "");
  if (!isSigninSheetsForceQuery(search)) return;
  if (!canAutoStartSigninSheetsReconnect()) return;

  stageSigninSheetsReconnectFromSearch(search);

  const options = readSigninSheetsReconnectOptions(search);

  const navigateOpts = {
    requireForce: true as const,
    ...(options.auto ? { requireAuto: true as const } : {}),
    fresh: true as const,
  };

  try {
    const navigated = await tryNavigateSigninSheetsReconnectCompleteIfConnected(
      search,
      navigateOpts,
    );
    if (navigated) return;

    const next = new URL(window.location.href);
    next.searchParams.delete("force");
    const qs = next.searchParams.toString();
    window.location.replace(`/signin/sheets-expired${qs ? `?${qs}` : ""}`);
  } catch {
    window.location.replace("/signin/sheets-expired?auto=1");
  }
}

/**
 * Logout from sheets-expired/complete success — stage force resume and return to
 * `/signin?callbackUrl=/&sheetsReconnectResume=1` so refresh resumes the flow.
 */
export function releaseSigninSheetsReconnectFromCompleteWhenLoggedOut(): void {
  if (typeof window === "undefined") return;

  const forceReturnPath = buildSigninSheetsForceAutoEntryPath({ auto: true });
  persistSigninSheetsReconnectAfterLoginPath(forceReturnPath);
  const forceQuery = forceReturnPath.includes("?")
    ? forceReturnPath.slice(forceReturnPath.indexOf("?"))
    : "";
  if (forceQuery) {
    stageSigninSheetsReconnectFromSearch(forceQuery);
  } else {
    clearSigninSheetsReconnectIntent();
  }
  clearSigninSheetsReconnectAwaitingOAuth();
  clearSigninOAuthLoginNextPath();
  clearSigninPendingCallbackUrl();

  const signinPath = `/signin?callbackUrl=${encodeURIComponent("/")}&sheetsReconnectResume=1`;
  void navigateToSigninPathWithLineProfileWarmup(signinPath);
}

/**
 * Cross-tab logout on sheets reconnect — return to `/signin?callbackUrl=…`
 * (same spirit as AuthWrapper), not a bare `/signin`.
 */
export function releaseSigninSheetsForceEntryWhenLoggedOut(): boolean {
  if (typeof window === "undefined") return false;
  if (hasClientAuthToken()) return false;

  const search = window.location.search || "";
  const onSheetsExpired = window.location.pathname.startsWith(
    "/signin/sheets-expired",
  );
  const onForceSignin =
    window.location.pathname === "/signin" && isSigninSheetsForceQuery(search);
  if (!onForceSignin && !onSheetsExpired) return false;

  const options = readSigninSheetsReconnectOptions(search);
  const forceReturnPath = buildSigninSheetsForceAutoEntryPath({
    auto: options.auto,
    serviceId: options.serviceId,
    ...(options.callbackUrl ? { callbackUrl: options.callbackUrl } : {}),
  });
  persistSigninSheetsReconnectAfterLoginPath(forceReturnPath);
  const forceQuery = forceReturnPath.includes("?")
    ? forceReturnPath.slice(forceReturnPath.indexOf("?"))
    : "";
  if (forceQuery) {
    stageSigninSheetsReconnectFromSearch(forceQuery);
  } else {
    clearSigninSheetsReconnectIntent();
  }
  clearSigninSheetsReconnectAwaitingOAuth();
  clearSigninSheetsReconnectWorkspaceId();

  const signinPath = buildSigninUrlWithWorkspaceCallback(search);

  try {
    const current = `${window.location.pathname}${window.location.search}`;
    if (current === signinPath) return false;
    void navigateToSigninPathWithLineProfileWarmup(signinPath);
    return true;
  } catch {
    void navigateToSigninPathWithLineProfileWarmup("/signin");
    return true;
  }
}

export function buildLoginExternalIntegrationsPath(
  options: SigninSheetsReconnectOptions,
): string {
  const cancelTo = buildSigninSheetsReconnectCancelTo(options);
  return buildGoogleIntegrationsExternalPath({
    serviceId: options.serviceId,
    callbackUrl: resolveSigninSheetsReconnectCallbackUrl(options),
    auto: options.auto,
    cancelTo,
    sheetsReconnect: true,
  });
}

// Cross-tab logout: prevent stale Sheets-force resume from hijacking normal /signin flows.
if (typeof window !== "undefined") {
  window.addEventListener("auth:logout", () => {
    try {
      // Complete / sheets-expired pages re-stage reconnect after logout — do not wipe here.
      if (window.location.pathname.startsWith("/signin/sheets-expired")) {
        return;
      }
      clearSigninSheetsReconnectAfterLoginPath();
      clearSigninSheetsReconnectIntent();
      clearSigninSheetsReconnectAwaitingOAuth();
      clearSigninSheetsReconnectWorkspaceId();
    } catch {
      // ignore
    }
  });
}

function buildSigninSheetsReconnectCancelTo(
  options: SigninSheetsReconnectOptions,
): string {
  const p = new URLSearchParams();
  if (options.auto) p.set("auto", "1");
  const sid = options.serviceId?.trim();
  if (sid && sid !== BILLIN_SERVICE_ID) p.set("serviceId", sid);
  if (options.callbackUrl) p.set("callbackUrl", options.callbackUrl);
  const qs = p.toString();
  return `/signin/sheets-expired${qs ? `?${qs}` : ""}`;
}

/** Navigate to Google Drive/Sheets integrations OAuth (same as FinancialAdmin connect). */
export function navigateToSigninSheetsIntegrations(
  options: SigninSheetsReconnectOptions,
  onNavigate?: (path: string) => void,
): void {
  // Safety net: auto flow must never start without a usable JWT.
  // If called (directly or indirectly) while signed out, bounce to `/signin` first
  // and resume after login.
  if (options.auto && !canAutoStartSigninSheetsReconnect()) {
    redirectToSigninForSheetsReconnectLogin(options);
    return;
  }
  navigateToGoogleIntegrationsConnect(
    {
      serviceId: options.serviceId,
      callbackUrl: resolveSigninSheetsReconnectCallbackUrl(options),
      auto: options.auto,
      cancelTo: buildSigninSheetsReconnectCancelTo(options),
      sheetsReconnect: true,
    },
    onNavigate,
  );
}
